import jsPDF from 'jspdf';
import scLogoSrc from '../../Assets/SC_logo.png';

const COMPANY = {
  name: 'Square Computers',
  address: 'KV HOLLA BUILDING, KARANTH STREET, Saligrama',
  phone: '9164915365',
  email: 'squaresaligrama@gmail.com',
  signedBy: 'Sharath Kumar',
  website: 'https://squarecomputers.in',
};

// Where /verify/{number} lives — used for the "Click here to verify" link.
const VERIFY_BASE = 'https://squarecomputers.in/verify';

// Column x positions (A4 = 210mm, left/right margins at 14mm)
// # : 14–23 | Item name : 24–112 | Qty : 112–140 | Price/unit : 140–169 | Amount : 169–196
const C = {
  serial: { center: 18 },
  name:   { left: 24, maxW: 86 },
  qty:    { center: 126 },
  price:  { right: 169 },
  amount: { right: 196 },
};

// Padding inside each table row (mm from separator to text baseline)
const PAD_TOP = 5;  // 5mm → visual cap-height gap ≈ 2.7mm
const PAD_BOT = 3;  // 3mm below last baseline before next separator

// Leave room for signature at bottom
const PAGE_MARGIN_BOT = 26;

function fmtAmt(n) {
  return 'Rs. ' + Number(n).toLocaleString('en-IN');
}

async function toGrayscaleBase64(src) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        const gray = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
        d[i] = d[i + 1] = d[i + 2] = gray;
      }
      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export function calcTotal(items) {
  return (items || []).reduce(
    (sum, it) => sum + (Number(it.quantity || 1) * Number(it.price || 0)),
    0
  );
}

export function resolveTotal(bill) {
  const t = Number(bill.totalPrice);
  return isNaN(t) ? calcTotal(bill.items) : t;
}

// Renders one warranty line with mixed bold/normal text and word-wrap.
// "- [item name] has [bold: warranty] warranty starting from [bold: date]"
// Returns the y position ready for the next warranty line.
function drawWarrantyItem(doc, itemName, warrantyDuration, date, x0, pageW, pageH, startY) {
  const rightEdge = pageW - 14;
  const lineH = 5;
  const bulletR  = 0.85;  // filled circle radius (mm)
  const bulletGap = 4.5;  // indent after bullet to start text
  const textX = x0 + bulletGap;

  const parts = [
    { text: (itemName || '') + ' has ', bold: false },
    { text: warrantyDuration || '', bold: true  },
    { text: ' warranty starting from ', bold: false },
    { text: date, bold: true  },
  ];

  doc.setFontSize(9);
  let cx = textX;
  let cy = startY;

  // Bold filled circle bullet aligned to first text line
  doc.setFillColor(0, 0, 0);
  doc.circle(x0 + bulletR + 0.3, cy - 1.2, bulletR, 'F');

  for (const { text, bold } of parts) {
    if (!text) continue;
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const tokens = text.split(/(\s+)/);
    for (const tok of tokens) {
      if (!tok) continue;
      const isSpace = /^\s+$/.test(tok);
      const tw = doc.getTextWidth(tok);
      if (isSpace) {
        if (cx > textX) cx += tw;
      } else {
        if (cx + tw > rightEdge && cx > textX) {
          cy += lineH;
          if (cy > pageH - PAGE_MARGIN_BOT) { doc.addPage(); cy = 20; }
          cx = textX;
        }
        doc.text(tok, cx, cy);
        cx += tw;
      }
    }
  }
  return cy + lineH + 3;
}

// Draws the column header band; returns the y after the band + start gap
function drawTableHeader(doc, y, pageW) {
  doc.setFillColor(240, 240, 240);
  doc.rect(14, y, pageW - 28, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  const hY = y + 5.5;
  doc.text('#',          C.serial.center, hY, { align: 'center' });
  doc.text('Item name',  C.name.left,     hY);
  doc.text('Qty',        C.qty.center,    hY, { align: 'center' });
  doc.text('Price/unit', C.price.right,   hY, { align: 'right' });
  doc.text('Amount',     C.amount.right,  hY, { align: 'right' });
  y += 8;
  doc.setLineWidth(0.3);
  doc.line(14, y, pageW - 14, y);
  return y + PAD_TOP; // first row text starts here
}

export async function buildBillPDF(
  invoiceNumber, customerName, phoneNumber, date, items, total,
  address = ''   // optional — existing bills without address still work
) {
  const logoBase64 = await toGrayscaleBase64(scLogoSrc);
  // compress: true → lossless Flate compression of the PDF streams (smaller
  // file, identical quality).
  const doc = new jsPDF({ compress: true });
  const pageW = doc.internal.pageSize.getWidth();   // 210
  const pageH = doc.internal.pageSize.getHeight();  // 297
  let y = 12;

  // ── Logo — proportional fit within 86 × 46 mm (≈30 % larger, moved up) ─
  if (logoBase64) {
    const props = doc.getImageProperties(logoBase64);
    const maxW = 112, maxH = 60;
    const scale = Math.min(maxW / props.width, maxH / props.height);
    const logoW = props.width * scale;
    const logoH = props.height * scale;
    doc.addImage(logoBase64, 'PNG', pageW - 14 - logoW, -8, logoW, logoH);
  }

  // ── Company header ────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text(COMPANY.name, 14, y);
  y += 7;

  // Address and phone are BOLD per design
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  doc.text(COMPANY.address, 14, y);   y += 5;
  doc.text('Phone: ' + COMPANY.phone, 14, y); y += 5;

  // Email stays normal weight (less critical)
  doc.setFont('helvetica', 'normal');
  doc.text('Email: ' + COMPANY.email, 14, y);

  y += 8;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.4);
  doc.line(14, y, pageW - 14, y);
  y += 10;

  // ── Title ─────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(0, 0, 0);
  doc.text('Bill Invoice', pageW / 2, y, { align: 'center' });
  y += 14;

  // ── Bill To / Invoice Details ─────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Bill To', 14, y);
  doc.text('Invoice Details', pageW - 14, y, { align: 'right' });
  y += 7;

  // Right column: invoice number + date (stacked, right-aligned)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Invoice No.: ' + invoiceNumber, pageW - 14, y,     { align: 'right' });
  doc.text('Date: ' + date,                 pageW - 14, y + 5, { align: 'right' });

  // Left column: customer name → phone → address (bold black, no labels)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  const nameLines = doc.splitTextToSize(customerName, 88);
  nameLines.forEach((line, i) => doc.text(line, 14, y + i * 5));
  let leftY = y + nameLines.length * 5;

  doc.text(phoneNumber, 14, leftY);
  leftY += 5;

  if (address && address.trim()) {
    const addrLines = doc.splitTextToSize(address.trim(), 88);
    addrLines.forEach((ln, i) => doc.text(ln, 14, leftY + i * 5));
    leftY += addrLines.length * 5;
  }

  y = leftY + 8; // gap before table

  // ── Table top border ─────────────────────────────────────────────────
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.4);
  doc.line(14, y, pageW - 14, y);
  y += 1;

  // Draw table header; returns y ready for first row text
  y = drawTableHeader(doc, y, pageW);

  // ── Item rows ─────────────────────────────────────────────────────────
  doc.setFontSize(9);

  items.forEach((item, i) => {
    const nameW = doc.splitTextToSize(item.itemName || '', C.name.maxW);
    const hasSerial = !!(item.serialNumber && String(item.serialNumber).trim());
    const SERIAL_GAP = 4; // extra mm reserved below the name block for the serial number line
    const rowH = nameW.length * 5 + (hasSerial ? SERIAL_GAP : 0) + PAD_TOP + PAD_BOT;

    // Page-break check: if the full row doesn't fit, start a new page
    if (y + rowH > pageH - PAGE_MARGIN_BOT) {
      doc.addPage();
      y = 14;
      // Continuation: table top border + header
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.4);
      doc.line(14, y, pageW - 14, y);
      y += 1;
      y = drawTableHeader(doc, y, pageW);
    }

    // textTop: baseline of FIRST text line in the row
    const textTop = y;
    // midY: centre of the multi-line text block (for single-value cells)
    const midY = textTop + (nameW.length - 1) * 5 / 2;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(String(i + 1), C.serial.center, midY, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    nameW.forEach((ln, li) => doc.text(ln, C.name.left, textTop + li * 5));

    if (hasSerial) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(90, 90, 90);
      doc.text(`Serial No: ${item.serialNumber}`, C.name.left, textTop + (nameW.length - 1) * 5 + SERIAL_GAP);
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
    }

    doc.setFont('helvetica', 'normal');
    doc.text(String(item.quantity),                              C.qty.center,   midY, { align: 'center' });
    doc.text(fmtAmt(item.price),                                 C.price.right,  midY, { align: 'right'  });
    doc.text(fmtAmt(Number(item.quantity) * Number(item.price)), C.amount.right, midY, { align: 'right'  });

    // Advance y to cover text block + bottom padding
    y += nameW.length * 5 + (hasSerial ? SERIAL_GAP : 0) + PAD_BOT;

    // Light separator between rows (skip after last item)
    if (i < items.length - 1) {
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(14, y, pageW - 14, y);
      doc.setDrawColor(0, 0, 0);
      y += PAD_TOP; // top padding for next row
    }
  });

  // Table bottom border (after all rows)
  y += 2;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.4);
  doc.line(14, y, pageW - 14, y);
  y += 8;

  // ── Total ──────────────────────────────────────────────────────────────
  // Page-break check before total
  if (y + 16 > pageH - PAGE_MARGIN_BOT) {
    doc.addPage();
    y = 20;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Total', C.price.right, y, { align: 'right' });
  doc.text(fmtAmt(total), C.amount.right, y, { align: 'right' });
  y += 16;

  // ── Warranty Details — with bullets and bold duration / date ────────────
  const warrantyItems = items.filter(it => it.warranty && it.warranty !== 'No');
  if (warrantyItems.length > 0) {
    if (y + 20 > pageH - PAGE_MARGIN_BOT) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Warranty Details', 14, y);
    y += 9;
    warrantyItems.forEach(it => {
      if (y + 8 > pageH - PAGE_MARGIN_BOT) { doc.addPage(); y = 20; }
      y = drawWarrantyItem(doc, it.itemName, it.warranty, date, 14, pageW, pageH, y);
    });
  }

  // ── Footer: genuine-invoice verify line (left) + digital signature (right) ─
  const lastPageH = doc.internal.pageSize.getHeight();
  const footerY = lastPageH - 16;

  // Genuine-invoice verify line (left) — replaces the old "visit website" link
  // since both pointed to the same site.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  const vPre = 'This is a genuine invoice. ';
  doc.text(vPre, 14, footerY);
  const vPreW = doc.getTextWidth(vPre);
  doc.setTextColor(37, 99, 235);
  doc.textWithLink('Click here to verify', 14 + vPreW, footerY, { url: `${VERIFY_BASE}/${invoiceNumber}` });
  const vLinkW = doc.getTextWidth('Click here to verify');
  doc.setLineWidth(0.2);
  doc.setDrawColor(37, 99, 235);
  doc.line(14 + vPreW, footerY + 0.8, 14 + vPreW + vLinkW, footerY + 0.8);

  // Digital signature (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('Digitally signed by: ' + COMPANY.signedBy, pageW - 14, footerY, { align: 'right' });

  return doc.output('blob');
}
